import React, { useCallback, useEffect, useRef, useState } from "react";
import { withKey, stripKey } from "../lib/uid";
import {
  ReceiptText, Plus, Trash2, Pencil, FileDown, Printer, Eye, Send,
  CheckCircle2, Save, ArrowLeft, Loader2, Camera, X, MessageCircle,
} from "lucide-react";
import { toast } from "sonner";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { api, formatApiError, fileUrl } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { useConfirm } from "../hooks/useConfirm";

const fmtUSD = (v) => `$${Number(v || 0).toFixed(2)}`;
const todayISO = () => new Date().toISOString().slice(0, 10);

const EMPTY_ITEM = { item: "", description: "", unit_price: 0, quantity: 0, amount: 0 };

// Incrementa o último grupo de dígitos preservando o zero-padding. "0000078" -> "0000079"
function incrementNumber(str) {
  const s = String(str || "");
  const m = s.match(/(\d+)(?!.*\d)/);
  if (!m) return null;
  const num = m[1];
  const inc = String(parseInt(num, 10) + 1).padStart(num.length, "0");
  return s.slice(0, m.index) + inc + s.slice(m.index + num.length);
}

// Encontra o próximo número para um cliente a partir das faturas existentes.
function nextNumberForClient(invoices, clientId, clientName) {
  const name = (clientName || "").toLowerCase();
  const mine = (invoices || []).filter(
    (i) => (clientId && i.to_client_id === clientId) || (name && (i.to_name || "").toLowerCase() === name),
  );
  let bestNumber = null;
  let bestVal = -1;
  for (const inv of mine) {
    const groups = String(inv.number || "").match(/\d+/g);
    if (!groups) continue;
    const v = parseInt(groups[groups.length - 1], 10);
    if (v > bestVal) { bestVal = v; bestNumber = inv.number; }
  }
  return bestNumber != null ? incrementNumber(bestNumber) : null;
}

export default function CustomInvoices() {
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | builder
  const [editing, setEditing] = useState(null);
  const [company, setCompany] = useState(null);
  const [clients, setClients] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inv, comp, cli] = await Promise.all([
        api.get("/custom-invoices"),
        api.get("/company-settings"),
        api.get("/clients"),
      ]);
      setList(inv.data || []);
      setCompany(comp.data || {});
      setClients(cli.data || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function openNew() {
    let number = "";
    try {
      const { data } = await api.get("/custom-invoices/next-number");
      number = data.number;
    } catch { /* ignore */ }
    setEditing({ __new: true, number });
    setView("builder");
  }

  async function del(inv) {
    const ok = await confirm({
      title: `Delete invoice #${inv.number}?`,
      description: "This action is permanent.",
      destructive: true,
      actionLabel: "Delete",
    });
    if (!ok) return;
    try {
      await api.delete(`/custom-invoices/${inv.id}`);
      toast.success("Invoice deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }

  if (view === "builder") {
    return (
      <AdminLayout>
        <InvoiceBuilder
          invoice={editing}
          company={company}
          clients={clients}
          invoices={list}
          onBack={() => { setView("list"); setEditing(null); load(); }}
          onSaved={() => { load(); }}
        />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-5" data-testid="custom-invoices-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">Financial</div>
            <h1 className="text-2xl font-bold flex items-center gap-2"><ReceiptText className="text-blue-600" /> Invoices</h1>
            <p className="text-sm text-slate-500">Build invoices from scratch, generate PDF, print and send.</p>
          </div>
          <Button onClick={openNew} data-testid="new-invoice-btn" className="bg-blue-600 hover:bg-blue-700">
            <Plus size={16} className="mr-1" /> New Invoice
          </Button>
        </div>

        <div className="cm-card overflow-hidden">
          {loading ? (
            <div className="p-12 text-center text-slate-400">Loading…</div>
          ) : list.length === 0 ? (
            <div className="p-16 text-center text-slate-400" data-testid="invoices-empty">
              <ReceiptText size={40} className="mx-auto mb-3 text-slate-300" />
              No invoices yet. Click "New Invoice" to start.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="text-left p-3">#</th>
                    <th className="text-left p-3">Client</th>
                    <th className="text-left p-3">Date</th>
                    <th className="text-right p-3">Total</th>
                    <th className="text-right p-3">Balance</th>
                    <th className="text-center p-3">Status</th>
                    <th className="text-right p-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((inv) => (
                    <tr key={inv.id} data-testid={`invoice-row-${inv.id}`} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="p-3 font-mono text-slate-700">#{inv.number}</td>
                      <td className="p-3 font-medium">{inv.to_name || "—"}</td>
                      <td className="p-3 text-xs text-slate-500">{inv.invoice_date || "—"}</td>
                      <td className="p-3 text-right font-semibold">{fmtUSD(inv.total)}</td>
                      <td className="p-3 text-right">{fmtUSD(inv.balance_due)}</td>
                      <td className="p-3 text-center">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${inv.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-800 border-amber-200"}`}>
                          {inv.status === "paid" ? "Paid" : "Unpaid"}
                        </span>
                      </td>
                      <td className="p-3">
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => { setEditing(inv); setView("builder"); }} data-testid={`invoice-edit-${inv.id}`}
                            title="Edit" className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100">
                            <Pencil size={14} />
                          </button>
                          <button onClick={() => del(inv)} data-testid={`invoice-delete-${inv.id}`}
                            title="Delete" className="w-8 h-8 inline-flex items-center justify-center rounded-lg border border-red-200 bg-red-50 text-red-700 hover:bg-red-100">
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function InvoiceBuilder({ invoice, company, clients, invoices, onBack, onSaved }) {
  const sheetRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [id, setId] = useState(invoice?.id || null);
  const [showNameSug, setShowNameSug] = useState(false);
  const [numberAuto, setNumberAuto] = useState(false);

  const [f, setF] = useState(() => ({
    number: invoice?.number || "",
    from_name: invoice?.from_name ?? (company?.name || ""),
    from_address: invoice?.from_address ?? [company?.address, company?.phone, company?.email].filter(Boolean).join("\n"),
    logo_path: invoice?.logo_path ?? (company?.logo_path || ""),
    to_client_id: invoice?.to_client_id || "",
    to_name: invoice?.to_name || "",
    to_address: invoice?.to_address || "",
    to_email: invoice?.to_email || "",
    to_phone: invoice?.to_phone || "",
    invoice_date: invoice?.invoice_date || todayISO(),
    due_date: invoice?.due_date || todayISO(),
    items: invoice?.items?.length ? invoice.items.map((i) => withKey({ ...EMPTY_ITEM, ...i })) : [withKey(EMPTY_ITEM), withKey(EMPTY_ITEM)],
    notes: invoice?.notes || "",
    amount_paid: invoice?.amount_paid || 0,
  }));

  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  function setItem(idx, key, val) {
    setF((p) => {
      const items = p.items.map((it, i) => {
        if (i !== idx) return it;
        const next = { ...it, [key]: val };
        const up = key === "unit_price" ? Number(val) : Number(next.unit_price || 0);
        const qt = key === "quantity" ? Number(val) : Number(next.quantity || 0);
        next.amount = Math.round(up * qt * 100) / 100;
        return next;
      });
      return { ...p, items };
    });
  }
  const addLine = () => setF((p) => ({ ...p, items: [...p.items, withKey(EMPTY_ITEM)] }));
  const removeLine = (idx) => setF((p) => ({ ...p, items: p.items.filter((_, i) => i !== idx) }));

  async function pickClient(cid) {
    const c = clients.find((x) => x.id === cid);
    if (!c) { set("to_client_id", cid); return; }
    const addr = [c.address, [c.city, c.state].filter(Boolean).join(", "), c.zip_code].filter(Boolean).join("\n");
    const localNext = nextNumberForClient(invoices, cid, c.name);
    setF((p) => ({
      ...p,
      to_client_id: cid,
      to_name: c.name || "",
      to_address: addr,
      to_email: c.email || "",
      to_phone: c.phone || "",
      ...(localNext ? { number: localNext } : {}),
    }));
    setNumberAuto(!!localNext);
    try {
      const { data } = await api.get("/custom-invoices/client-next-number", { params: { client_id: cid, name: c.name } });
      if (data?.number) { setF((p) => ({ ...p, number: data.number })); setNumberAuto(true); }
    } catch { /* mantém sugestão local */ }
  }

  async function onLogo(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return toast.error("Please upload an image");
    setBusy("logo");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/public/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("logo_path", data.path);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy("");
    }
  }

  const subtotal = f.items.reduce((a, it) => a + Number(it.amount || 0), 0);
  const total = subtotal;
  const balanceDue = total - Number(f.amount_paid || 0);

  async function save() {
    if (!f.to_name.trim()) { toast.error("Enter the customer (Bill To)"); return null; }
    setSaving(true);
    try {
      const payload = { ...f, items: f.items.map(stripKey), amount_paid: Number(f.amount_paid || 0) };
      let res;
      if (id) res = await api.put(`/custom-invoices/${id}`, payload);
      else res = await api.post("/custom-invoices", payload);
      setId(res.data.id);
      if (res.data.number && res.data.number !== f.number) {
        setF((p) => ({ ...p, number: res.data.number }));
        toast.success(`Invoice saved as #${res.data.number} (adjusted to avoid a duplicate)`);
      } else {
        toast.success("Invoice saved");
      }
      setNumberAuto(false);
      onSaved?.();
      return res.data;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to save");
      return null;
    } finally {
      setSaving(false);
    }
  }

  async function captureCanvas() {
    const node = sheetRef.current;
    if (!node) return null;
    return html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true });
  }

  async function savePdfFile() {
    const canvas = await captureCanvas();
    if (!canvas) return false;
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ unit: "pt", format: "a4" });
    const pw = pdf.internal.pageSize.getWidth();
    const imgH = (canvas.height * pw) / canvas.width;
    pdf.addImage(img, "PNG", 0, 0, pw, imgH);
    pdf.save(`invoice-${f.number || "draft"}.pdf`);
    return true;
  }

  async function downloadPDF() {
    setBusy("pdf");
    try {
      await savePdfFile();
    } catch (e) {
      toast.error("Failed to generate PDF");
    } finally { setBusy(""); }
  }

  async function printOrPreview(mode) {
    setBusy(mode);
    try {
      const canvas = await captureCanvas();
      if (!canvas) return;
      const w = window.open("");
      const auto = mode === "print" ? "onload=\"setTimeout(function(){window.print()},250)\"" : "";
      w.document.write(`<html><head><title>Invoice ${f.number}</title></head><body style="margin:0"><img src="${canvas.toDataURL("image/png")}" style="width:100%" ${auto}/></body></html>`);
      w.document.close();
    } catch (e) {
      toast.error("Failed to open preview");
    } finally { setBusy(""); }
  }

  async function markPaid() {
    let cur = id ? { id } : await save();
    if (!cur) return;
    setBusy("paid");
    try {
      await api.post(`/custom-invoices/${cur.id}/mark-paid`);
      setF((p) => ({ ...p, amount_paid: total }));
      toast.success("Invoice marked as paid");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(""); }
  }

  async function sendEmail() {
    let cur = id ? { id } : await save();
    if (!cur) return;
    const to = window.prompt("Send invoice to which email?", f.to_email || "");
    if (!to) return;
    setBusy("send");
    try {
      await api.post(`/custom-invoices/${cur.id}/send`, { to_email: to });
      toast.success(`Invoice sent to ${to}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to send");
    } finally { setBusy(""); }
  }

  async function sendWhatsApp() {
    const digits = (f.to_phone || "").replace(/\D/g, "");
    if (!digits) { toast.error("Enter the customer's phone (WhatsApp)"); return; }
    let cur = id ? { id } : await save();
    if (!cur) return;
    const phone = digits.length === 10 ? "1" + digits : digits;
    setBusy("whatsapp");
    try {
      await savePdfFile();
      const msg =
        `Hi ${f.to_name || ""}! Here is invoice #${f.number} — ` +
        `Total ${fmtUSD(total)}, balance due ${fmtUSD(balanceDue)}. ` +
        `The PDF is attached. Thank you!`;
      window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
      toast.success("Opening WhatsApp — attach the PDF that was just downloaded.");
    } catch (e) {
      toast.error("Failed to prepare the message");
    } finally { setBusy(""); }
  }

  const inputBare = "w-full bg-transparent border-b border-slate-200 focus:border-blue-500 outline-none px-1 py-1 text-sm";

  return (
    <div className="space-y-4" data-testid="invoice-builder">
      {/* Action bar */}
      <div className="flex flex-wrap items-center justify-between gap-2 sticky top-0 z-10 bg-slate-50/90 backdrop-blur py-2">
        <button onClick={onBack} data-testid="invoice-back" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900">
          <ArrowLeft size={16} /> Back
        </button>
        <div className="flex flex-wrap gap-1.5">
          <ActionBtn onClick={() => printOrPreview("preview")} busy={busy === "preview"} icon={Eye} label="Preview" testId="invoice-preview" />
          <ActionBtn onClick={() => printOrPreview("print")} busy={busy === "print"} icon={Printer} label="Print" testId="invoice-print" />
          <ActionBtn onClick={downloadPDF} busy={busy === "pdf"} icon={FileDown} label="PDF" testId="invoice-pdf" />
          <ActionBtn onClick={sendEmail} busy={busy === "send"} icon={Send} label="Send" testId="invoice-send" />
          <ActionBtn onClick={sendWhatsApp} busy={busy === "whatsapp"} icon={MessageCircle} label="WhatsApp" testId="invoice-whatsapp" tone="green" />
          <ActionBtn onClick={markPaid} busy={busy === "paid"} icon={CheckCircle2} label="Mark as Paid" testId="invoice-mark-paid" tone="emerald" />
          <Button onClick={save} disabled={saving} data-testid="invoice-save" className="bg-blue-600 hover:bg-blue-700">
            {saving ? <Loader2 size={15} className="mr-1 animate-spin" /> : <Save size={15} className="mr-1" />} Save
          </Button>
        </div>
      </div>

      {/* The invoice sheet */}
      <div ref={sheetRef} className="bg-white border border-slate-200 rounded-xl p-6 sm:p-10 max-w-3xl mx-auto shadow-sm" data-testid="invoice-sheet">
        {/* Header: logo left, company right */}
        <div className="flex items-start justify-between gap-4 pb-6 border-b border-slate-100">
          <div>
            {f.logo_path ? (
              <div className="relative inline-block">
                <img src={fileUrl(f.logo_path)} alt="logo" className="h-20 w-auto max-w-[200px] object-contain" data-testid="invoice-logo" crossOrigin="anonymous" />
                <button type="button" onClick={() => set("logo_path", "")} className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-white border shadow flex items-center justify-center text-rose-600 no-export" aria-label="Remove logo">
                  <X size={13} />
                </button>
              </div>
            ) : (
              <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-dashed border-slate-300 text-slate-500 text-sm cursor-pointer hover:bg-slate-50 no-export" data-testid="invoice-logo-btn">
                {busy === "logo" ? <Loader2 size={15} className="animate-spin" /> : <Camera size={15} />} Company logo
                <input type="file" accept="image/*" className="hidden" onChange={onLogo} data-testid="invoice-logo-input" />
              </label>
            )}
          </div>
          <div className="text-right flex-1 max-w-xs">
            <input value={f.from_name} onChange={(e) => set("from_name", e.target.value)} data-testid="invoice-from-name"
              className="w-full bg-transparent text-right font-bold text-lg text-slate-800 outline-none" placeholder="Your company" />
            <textarea value={f.from_address} onChange={(e) => set("from_address", e.target.value)} data-testid="invoice-from-address"
              rows={3} className="w-full bg-transparent text-right text-xs text-slate-500 outline-none resize-none mt-1" placeholder="Address, phone, email" />
          </div>
        </div>

        <div className="text-right my-4">
          <span className="text-3xl font-light tracking-[0.3em] text-slate-400">INVOICE</span>
        </div>

        {/* Bill To + meta */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Bill To</div>
            <div className="relative">
              <input
                value={f.to_name}
                data-testid="invoice-to-name"
                autoComplete="off"
                onChange={(e) => { set("to_name", e.target.value); set("to_client_id", ""); setShowNameSug(true); }}
                onFocus={() => setShowNameSug(true)}
                onBlur={() => setTimeout(() => setShowNameSug(false), 150)}
                className={inputBare + " font-semibold text-slate-800"}
                placeholder="Type customer name (e.g. Ca…)"
              />
              {showNameSug && f.to_name.trim() && (() => {
                const term = f.to_name.trim().toLowerCase();
                const matches = clients
                  .filter((c) => c.name && c.name.toLowerCase().includes(term) && c.name.toLowerCase() !== term)
                  .slice(0, 8);
                if (matches.length === 0) return null;
                return (
                  <ul data-testid="invoice-name-suggestions" className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-56 overflow-auto no-export">
                    {matches.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          data-testid={`invoice-name-sug-${c.id}`}
                          onMouseDown={(e) => { e.preventDefault(); pickClient(c.id); setShowNameSug(false); }}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-blue-50"
                        >
                          <span className="font-medium text-slate-800">{c.name}</span>
                          {c.city && <span className="text-xs text-slate-400 ml-2">{c.city}</span>}
                        </button>
                      </li>
                    ))}
                  </ul>
                );
              })()}
            </div>
            <textarea value={f.to_address} onChange={(e) => set("to_address", e.target.value)} data-testid="invoice-to-address"
              rows={2} className="w-full bg-transparent border-b border-slate-200 focus:border-blue-500 outline-none px-1 py-1 text-xs text-slate-500 resize-none mt-1" placeholder="Customer address" />
            <div className="mt-2 no-export">
              <input value={f.to_phone} onChange={(e) => set("to_phone", e.target.value)} data-testid="invoice-to-phone"
                className="w-full rounded-md border border-slate-200 px-2 py-1.5 text-xs text-slate-600 focus:border-blue-500 outline-none" placeholder="Phone for WhatsApp (e.g. (555) 123-4567)" />
            </div>
          </div>
          <div className="space-y-1.5 text-sm">
            <MetaRow label="Invoice #">
              <input value={f.number} onChange={(e) => { set("number", e.target.value); setNumberAuto(false); }} data-testid="invoice-number"
                title={numberAuto ? "Suggested next number for this client (editable)" : undefined}
                className={`w-32 text-right bg-transparent border-b outline-none px-1 font-mono ${numberAuto ? "border-green-400 text-green-600 font-bold" : "border-slate-200 focus:border-blue-500"}`} />
            </MetaRow>
            <MetaRow label="Invoice Date">
              <input type="date" value={f.invoice_date} onChange={(e) => set("invoice_date", e.target.value)} data-testid="invoice-date"
                className="w-40 text-right bg-transparent border-b border-slate-200 focus:border-blue-500 outline-none px-1" />
            </MetaRow>
            <MetaRow label="Due Date">
              <input type="date" value={f.due_date} onChange={(e) => set("due_date", e.target.value)} data-testid="invoice-due-date"
                className="w-40 text-right bg-transparent border-b border-slate-200 focus:border-blue-500 outline-none px-1" />
            </MetaRow>
          </div>
        </div>

        {/* Items table */}
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-blue-600 text-white text-xs">
              <th className="text-left p-2 font-semibold rounded-l">Item</th>
              <th className="text-left p-2 font-semibold">Description</th>
              <th className="text-right p-2 font-semibold w-24">Unit Price</th>
              <th className="text-right p-2 font-semibold w-20">Quantity</th>
              <th className="text-right p-2 font-semibold w-24 rounded-r">Amount</th>
              <th className="w-8 no-export"></th>
            </tr>
          </thead>
          <tbody data-testid="invoice-items">
            {f.items.map((it, idx) => (
              <tr key={it._k} className="border-b border-slate-100">
                <td className="p-1"><input value={it.item} onChange={(e) => setItem(idx, "item", e.target.value)} data-testid={`item-name-${idx}`} className="w-full bg-transparent outline-none px-1 py-1" /></td>
                <td className="p-1"><input value={it.description} onChange={(e) => setItem(idx, "description", e.target.value)} data-testid={`item-desc-${idx}`} className="w-full bg-transparent outline-none px-1 py-1" /></td>
                <td className="p-1"><input type="number" step="0.01" min="0" value={it.unit_price || ""} onChange={(e) => setItem(idx, "unit_price", e.target.value)} data-testid={`item-price-${idx}`} className="w-full bg-transparent outline-none px-1 py-1 text-right" placeholder="0.00" /></td>
                <td className="p-1"><input type="number" step="1" min="0" value={it.quantity || ""} onChange={(e) => setItem(idx, "quantity", e.target.value)} data-testid={`item-qty-${idx}`} className="w-full bg-transparent outline-none px-1 py-1 text-right" placeholder="0" /></td>
                <td className="p-1 text-right font-medium text-slate-700" data-testid={`item-amount-${idx}`}>{fmtUSD(it.amount)}</td>
                <td className="p-1 text-center no-export">
                  {f.items.length > 1 && (
                    <button onClick={() => removeLine(idx)} data-testid={`item-remove-${idx}`} className="text-slate-300 hover:text-rose-500"><X size={15} /></button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <button onClick={addLine} data-testid="invoice-add-line" className="mt-2 inline-flex items-center gap-1.5 text-sm text-blue-600 hover:text-blue-700 font-medium no-export">
          <Plus size={15} /> New Line
        </button>

        {/* Notes + totals */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Notes</div>
            <textarea value={f.notes} onChange={(e) => set("notes", e.target.value)} data-testid="invoice-notes"
              rows={4} className="w-full rounded-lg border border-slate-200 focus:border-blue-500 outline-none p-2 text-sm resize-none" placeholder="Notes, payment instructions…" />
          </div>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between"><span className="font-semibold text-slate-600">Subtotal</span><span data-testid="invoice-subtotal" className="font-medium">{fmtUSD(subtotal)}</span></div>
            <div className="flex items-center justify-between"><span className="font-semibold text-slate-600">Total</span><span data-testid="invoice-total" className="font-medium">{fmtUSD(total)}</span></div>
            <div className="flex items-center justify-between">
              <span className="font-semibold text-slate-600">Amount Paid</span>
              <span className="inline-flex items-center">$
                <input type="number" step="0.01" min="0" value={f.amount_paid || ""} onChange={(e) => set("amount_paid", e.target.value)} data-testid="invoice-amount-paid"
                  className="w-24 text-right bg-transparent border-b border-slate-200 focus:border-blue-500 outline-none px-1" placeholder="0.00" />
              </span>
            </div>
            <div className="flex items-center justify-between bg-yellow-100 px-3 py-2 rounded">
              <span className="font-bold text-slate-800">Balance Due</span>
              <span data-testid="invoice-balance-due" className="font-bold text-slate-900">{fmtUSD(balanceDue)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActionBtn({ onClick, busy, icon: Icon, label, testId, tone }) {
  const cls = tone === "emerald"
    ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
    : tone === "green"
    ? "border-green-200 bg-green-50 text-green-700 hover:bg-green-100"
    : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50";
  return (
    <button onClick={onClick} disabled={busy} data-testid={testId}
      className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm font-medium disabled:opacity-50 ${cls}`}>
      {busy ? <Loader2 size={15} className="animate-spin" /> : <Icon size={15} />} <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function MetaRow({ label, children }) {
  return (
    <div className="flex items-center justify-end gap-3">
      <span className="font-semibold text-slate-600">{label}</span>
      {children}
    </div>
  );
}
